// src/pages/SuperAdmin/index.jsx
import React, { useState, useEffect } from 'react';
import { Container, Nav, NavItem, NavLink } from 'reactstrap';
import { get } from 'helpers/api_helper';
import { useHistory, useParams } from 'react-router-dom';
import { isSuperAdminUser } from 'helpers/permissionHelper';

import SuperAdminDashboard from './SuperAdminDashboard';
import TenantsManager from './TenantsManager';
import PlansManager from './PlansManager';
import SubscriptionsManager from './SubscriptionsManager';
import PaymentsManager from './PaymentsManager';
import DropdownsManager from './DropdownsManager';
import RxTemplatesManager from './RxTemplatesManager';
import TemplateStudio from './TemplateStudio';
import GlobalCommunicationManager from './GlobalCommunicationManager';
import EventsAndBannersManager from './EventsAndBannersManager';

import './superadmin.scss';

const SuperAdmin = () => {
  const history = useHistory();
  const params = useParams();
  const routeTab = params.tab || 'dashboard';
  const subParam = params.subParam || null;

  const [activeTab, setActiveTab] = useState(routeTab);
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);

  // Sync tab state when URL route changes
  useEffect(() => {
    if (params.tab && params.tab !== activeTab) {
      setActiveTab(params.tab);
    }
  }, [params.tab]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    history.push(`/superadmin/${newTab}`);
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, tenantsRes, plansRes, subsRes, paymentsRes] = await Promise.all([
        get('/superadmin/dashboard-stats').catch(() => null),
        get('/superadmin/tenants').catch(() => null),
        get('/superadmin/plans').catch(() => null),
        get('/superadmin/subscriptions').catch(() => null),
        get('/superadmin/payments').catch(() => null),
      ]);

      const statsData = statsRes?.body || statsRes?.data || statsRes || null;
      const tenantsData = tenantsRes?.body || tenantsRes?.data || tenantsRes || [];
      const plansPayload = plansRes?.body || plansRes?.data || plansRes || {};
      const subsData = subsRes?.body || subsRes?.data || subsRes || [];
      const paymentsData = paymentsRes?.body || paymentsRes?.data || paymentsRes || [];

      setStats(statsData);
      setTenants(Array.isArray(tenantsData) ? tenantsData : []);
      setPlans(Array.isArray(plansPayload?.plans) ? plansPayload.plans : Array.isArray(plansPayload) ? plansPayload : []);
      setModules(Array.isArray(plansPayload?.modules) ? plansPayload.modules : []);
      setSubscriptions(Array.isArray(subsData) ? subsData : []);
      setPayments(Array.isArray(paymentsData) ? paymentsData : []);
    } catch (err) {
      console.error('Failed to load SuperAdmin data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdminUser()) {
      history.replace('/dashboard');
      return;
    }
    fetchAllData();
  }, [history]);

  if (!isSuperAdminUser()) {
    return null;
  }

  return (
    <div className="page-content superadmin-container">
      <Container fluid>
        {/* SuperAdmin Header */}
        <div className="superadmin-header d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h4 className="font-size-18 font-weight-bold text-dark mb-0">
                Oralstop SuperAdmin Command Center
              </h4>
              <span className="header-badge">PLATFORM SAAS OWNER</span>
            </div>
            <p className="text-muted font-size-12 mb-0">
              Manage multi-tenant dental clinics, configure subscription pricing & feature tiers, and track platform dropdown masters.
            </p>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-dark font-size-12 d-inline-flex align-items-center gap-1"
              onClick={async () => {
                if (!window.confirm("Seed complete master catalogs (Treatments, Rx Templates, Symptoms & Complaints, Diagnoses, Communication Groups, Patient Tags, Languages) for all clinics?")) return;
                try {
                  const res = await (await import('helpers/api_helper')).post('/superadmin/seed-all-master-data', {});
                  if (res?.success) {
                    const { showSuccessAlert } = await import('pages/utils/alertMessages');
                    showSuccessAlert("All standard clinical & administrative masters seeded successfully!");
                  }
                } catch (e) {
                  alert("Failed to seed master catalogs");
                }
              }}
              title="Seed Treatments, Rx Templates, Symptoms, Groups, Tags & Languages across all clinics"
            >
              <i className="bx bx-data font-size-14 text-warning" />
              <span>Seed All Master Catalogs</span>
            </button>

            <button
              type="button"
              className="btn btn-sm btn-outline-dark font-size-12 d-inline-flex align-items-center gap-1"
              onClick={fetchAllData}
              disabled={loading}
            >
              <i className={`bx bx-refresh font-size-14 ${loading ? 'bx-spin' : ''}`} />
              <span>Refresh Data</span>
            </button>

            <a
              href="/dashboard"
              className="btn btn-sm btn-primary font-size-12 d-inline-flex align-items-center gap-1 font-weight-semibold shadow-2xs"
            >
              <i className="bx bx-home-alt font-size-14" />
              <span>Switch to Clinic Workspace</span>
            </a>
          </div>
        </div>

        {/* Master SuperAdmin Navigation Tabs */}
        <Nav tabs className="nav-tabs-custom">
          <NavItem>
            <NavLink
              className={activeTab === 'dashboard' ? 'active' : ''}
              onClick={() => handleTabChange('dashboard')}
              style={{ cursor: 'pointer' }}
            >
              <i className="bx bxs-dashboard" />
              <span>Overview & KPIs</span>
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={activeTab === 'tenants' ? 'active' : ''}
              onClick={() => handleTabChange('tenants')}
              style={{ cursor: 'pointer' }}
            >
              <i className="bx bx-buildings" />
              <span>Tenants & Clinics ({tenants.length})</span>
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={activeTab === 'plans' ? 'active' : ''}
              onClick={() => handleTabChange('plans')}
              style={{ cursor: 'pointer' }}
            >
              <i className="bx bx-package" />
              <span>Subscription Tiers ({plans.length})</span>
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={activeTab === 'subscriptions' ? 'active' : ''}
              onClick={() => handleTabChange('subscriptions')}
              style={{ cursor: 'pointer' }}
            >
              <i className="bx bx-receipt" />
              <span>Subscriptions Ledger ({subscriptions.length})</span>
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={activeTab === 'payments' ? 'active' : ''}
              onClick={() => handleTabChange('payments')}
              style={{ cursor: 'pointer' }}
            >
              <i className="bx bx-money" />
              <span>Payments & Invoices ({payments.length})</span>
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={activeTab === 'rx-templates' ? 'active' : ''}
              onClick={() => handleTabChange('rx-templates')}
              style={{ cursor: 'pointer' }}
            >
              <i className="bx bx-capsule" />
              <span>Master Rx Protocols</span>
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={activeTab === 'templates' ? 'active' : ''}
              onClick={() => handleTabChange('templates')}
              style={{ cursor: 'pointer' }}
            >
              <i className="bx bx-palette" />
              <span>Print & Document Studio</span>
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={activeTab === 'communication' ? 'active' : ''}
              onClick={() => handleTabChange('communication')}
              style={{ cursor: 'pointer' }}
            >
              <i className="bx bx-broadcast" />
              <span>Global Communication</span>
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={activeTab === 'banners' || activeTab === 'events' ? 'active' : ''}
              onClick={() => handleTabChange('banners')}
              style={{ cursor: 'pointer' }}
            >
              <i className="bx bx-megaphone text-warning" />
              <span>Top Banners & Events</span>
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={activeTab === 'dropdowns' ? 'active' : ''}
              onClick={() => handleTabChange('dropdowns')}
              style={{ cursor: 'pointer' }}
            >
              <i className="bx bx-list-check" />
              <span>System Dropdowns & Masters</span>
            </NavLink>
          </NavItem>
        </Nav>

        {/* Render Tab Contents */}
        {activeTab === 'dashboard' && (
          <SuperAdminDashboard
            stats={stats}
            loading={loading}
            setActiveTab={handleTabChange}
          />
        )}

        {activeTab === 'tenants' && (
          <TenantsManager
            tenants={tenants}
            plans={plans}
            loading={loading}
            onRefresh={fetchAllData}
          />
        )}

        {activeTab === 'plans' && (
          <PlansManager
            plans={plans}
            modules={modules}
            loading={loading}
            onRefresh={fetchAllData}
          />
        )}

        {activeTab === 'subscriptions' && (
          <SubscriptionsManager
            subscriptions={subscriptions}
            plans={plans}
            loading={loading}
            onRefresh={fetchAllData}
          />
        )}

        {activeTab === 'payments' && (
          <PaymentsManager
            payments={payments}
            tenants={tenants}
            loading={loading}
            onRefresh={fetchAllData}
          />
        )}

        {activeTab === 'rx-templates' && (
          <RxTemplatesManager tenants={tenants} />
        )}

        {activeTab === 'templates' && (
          <TemplateStudio />
        )}

        {activeTab === 'communication' && (
          <GlobalCommunicationManager />
        )}

        {(activeTab === 'banners' || activeTab === 'events') && (
          <EventsAndBannersManager />
        )}

        {activeTab === 'dropdowns' && (
          <DropdownsManager initialCategory={subParam} onCategoryChange={(cat) => history.replace(`/superadmin/dropdowns/${cat}`)} />
        )}
      </Container>
    </div>
  );
};

export default SuperAdmin;
